// One appearance per login (GET/PUT /api/account/appearance): the rule the
// web, the iPhone app and the Mac app (whose window is the web) all apply,
// kept pure so it can be tested on its own (appearanceSync.selftest.ts).
// The iPhone's copy of the same rule is LatticeCore's AppearanceSync.
//
// Each device keeps its own copy of the theme so the first paint never
// waits for the network (the web: stores/ui.ts in localStorage). Then:
//   1. a choice made on this device and not yet on the server is sent
//      (it is newer than whatever the server held when it was read);
//   2. otherwise the server's theme wins when it has one: it is applied
//      here and kept locally;
//   3. when the server has none (never chosen), the first device with a
//      choice sends it. A device still on the default theme has made no
//      choice it could know of, so it sends nothing and waits.
// A theme name this build does not know (a newer build's) is never applied
// and never overwritten.

export interface AppearanceInputs {
  /** The theme this device shows now. */
  local: string;
  /** The login's theme on the server; null = never chosen. */
  server: string | null;
  /** A choice made here that the server has not confirmed yet. */
  pending: string | null;
  /** The theme names this build can show. */
  known: readonly string[];
  /** The default theme: a device showing it has made no choice. */
  fallback: string;
}

export interface AppearancePlan {
  /** Show this theme here (the server's). */
  apply: string | null;
  /** Send this theme to the server. */
  send: string | null;
  /** The pending choice is settled (the server has it) or void: forget it. */
  clearPending: boolean;
}

const NOTHING: AppearancePlan = { apply: null, send: null, clearPending: false };

export function planAppearance(i: AppearanceInputs): AppearancePlan {
  const knows = (name: string | null): name is string => name !== null && i.known.includes(name);
  const clearPending = i.pending !== null && (!knows(i.pending) || i.pending === i.server);
  if (knows(i.pending) && i.pending !== i.server) {
    return { apply: null, send: i.pending, clearPending: false };
  }
  if (i.server !== null) {
    if (!knows(i.server)) return { ...NOTHING, clearPending };
    return { apply: i.server !== i.local ? i.server : null, send: null, clearPending };
  }
  if (knows(i.local) && i.local !== i.fallback) {
    return { apply: null, send: i.local, clearPending };
  }
  return { ...NOTHING, clearPending };
}

// ─── The pending choice ───
//
// Kept in localStorage beside the ui store, per login, so a choice made
// while the server could not be reached is sent at the next focus, read or
// five-minute tick (and never sent for someone else who signs in here).

export const PENDING_KEY = "corvus:appearance";

/** The bits of Storage used here (a fake in the self-test). */
export type KeyValueStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface PendingRecord {
  user: string;
  theme: string;
}

function readRecord(store: KeyValueStore | null): PendingRecord | null {
  try {
    const raw = store?.getItem(PENDING_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<PendingRecord> | null;
    return v && typeof v.user === "string" && typeof v.theme === "string" ? { user: v.user, theme: v.theme } : null;
  } catch {
    return null;
  }
}

/** The choice this login made here and the server has not confirmed. */
export function pendingChoice(store: KeyValueStore | null, user: string): string | null {
  const rec = readRecord(store);
  return rec && rec.user === user ? rec.theme : null;
}

/** Remember a choice until the server has it (the latest choice replaces any earlier one). */
export function rememberChoice(store: KeyValueStore | null, user: string, theme: string): void {
  try {
    store?.setItem(PENDING_KEY, JSON.stringify({ user, theme } satisfies PendingRecord));
  } catch {
    // Private mode or a full store: the send still happens now, only a
    // failed one is not retried.
  }
}

/** Forget the pending choice, but only if it is still `theme` (a newer choice stays). */
export function settleChoice(store: KeyValueStore | null, user: string, theme: string): void {
  const rec = readRecord(store);
  if (!rec || rec.user !== user || rec.theme !== theme) return;
  try {
    store?.removeItem(PENDING_KEY);
  } catch {
    // Nothing to do: it is sent again next time, which changes nothing.
  }
}
